/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Body1, StyledFieldLabel } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { FileUploadComponent } from "@iventis/components/src/upload-file";
import { Autocomplete, TextField, Accordion, AccordionSummary, AccordionDetails, MenuItem, Select, FormControl } from "@mui/material";
import { EPSG_DEFS } from "@iventis/cad-georeferencer/src/constants/epsg-defs";
import { CadUploadConfigControlPoint } from "@iventis/domain-model/model/cadUploadConfigControlPoint";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { SitemapVersionLevelCadConfig } from "@iventis/domain-model/model/sitemapVersionLevelCadConfig";
import { SitemapVersionLevelUploadMethod } from "@iventis/domain-model/model/sitemapVersionLevelUploadMethod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SitemapVersionLevelStatus } from "@iventis/domain-model/model/sitemapVersionLevelStatus";
import { SitemapVersionLevelCadWithConfig } from "./sitemap-types";
import { cadConverterVersions } from "./sitemap-helpers";

export const SitemapVersionLevelCadForm = ({
    sitemapVersionLevel,
    setSitemapVersionLevel,
    getSitemapVersionLevelCadConfig,
    availableSitemapVersions,
    sitemapId,
}: {
    sitemapVersionLevel: SitemapVersionLevelCadWithConfig;
    setSitemapVersionLevel: (sitemapVersionLevel: SitemapVersionLevelCadWithConfig) => void;
    getSitemapVersionLevelCadConfig: (sitemapId: string, sitemapVersionId: string, sitemapVersionLevelId: string) => Promise<SitemapVersionLevelCadConfig>;
    availableSitemapVersions: SitemapVersion[];
    sitemapId: string;
}) => {
    const translate = useIventisTranslate();
    const [controlPointsError, setControlPointsError] = React.useState<boolean>(false);

    // Ensure cadConfig.version is always set to a valid value
    React.useEffect(() => {
        if (!sitemapVersionLevel.cadConfig?.version) {
            setSitemapVersionLevel({
                ...sitemapVersionLevel,
                cadConfig: {
                    ...sitemapVersionLevel.cadConfig,
                    version: Math.max(...cadConverterVersions),
                },
            });
        }
    }, [sitemapVersionLevel, setSitemapVersionLevel]);

    const text = `{(s0.X, s0.Y), .. (s2.X, s2.Y)}, {(t0.X, t0.Y), .. (t2.X, t2.Y)}`;
    return (
        <>
            <div>
                <StyledFieldLabel>{translate(Content.sitemaps.cad.cadFile)}</StyledFieldLabel>
                <FileUploadComponent
                    uploadFile={(file: File) => {
                        setSitemapVersionLevel({
                            ...sitemapVersionLevel,
                            status: SitemapVersionLevelStatus.Uploading,
                            cad: file,
                            // When uploading a new file, reset the cadConfig to default values as we are starting fresh
                            cadConfig: {
                                sitemapVersionLevelId: sitemapVersionLevel.id,
                                sourceEpsgCode: null,
                                controlPoints: null,
                                crop: null,
                                excludedLayerIds: [],
                                version: sitemapVersionLevel.cadConfig?.version ?? Math.max(...cadConverterVersions),
                            },
                        });
                    }}
                    removeRequested={() => setSitemapVersionLevel({ ...sitemapVersionLevel, cad: undefined })}
                    loading={false}
                    uploadButtonText={translate(Content.sitemaps.cad.upload)}
                    removeImageText={translate(Content.sitemaps.cad.remove)}
                    persistFileSelectInput
                    fileName={sitemapVersionLevel.cad?.name}
                />
            </div>

            <div>
                <StyledFieldLabel>{translate(Content.sitemaps.cad.preset.title)}</StyledFieldLabel>
                <Autocomplete
                    options={availableSitemapVersions
                        .flatMap((version) => version.sitemapVersionLevels)
                        .filter((level) => level.uploadMethod === SitemapVersionLevelUploadMethod.Cad)}
                    getOptionLabel={(option) => option.levelName}
                    groupBy={(option) => availableSitemapVersions?.find((v) => v.id === option.sitemapVersionId)?.name}
                    renderInput={(params) => <TextField {...params} placeholder={translate(Content.sitemaps.cad.preset.placeholder)} />}
                    onChange={async (_, value) => {
                        const config = value ? await getSitemapVersionLevelCadConfig(sitemapId, value.sitemapVersionId, value.id) : undefined;
                        if (config) {
                            setSitemapVersionLevel({
                                ...sitemapVersionLevel,
                                cadConfig: {
                                    ...sitemapVersionLevel.cadConfig,
                                    sourceEpsgCode: config.sourceEpsgCode,
                                    controlPoints: config.controlPoints,
                                    version: config.version, // Ensure the version is the same as the preset
                                },
                            });
                        }
                    }}
                    sx={{ width: "50%" }}
                />
            </div>

            <div>
                <StyledFieldLabel>{translate(Content.sitemaps.cad.coordinateSystem)}</StyledFieldLabel>
                <Autocomplete
                    options={EPSG_DEFS}
                    value={EPSG_DEFS.find((option) => option.code === sitemapVersionLevel.cadConfig?.sourceEpsgCode) || null}
                    getOptionLabel={(opt) => `${opt.name} - ${opt.code}`}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    renderInput={(params) => <TextField {...params} />}
                    onChange={(_, value) => setSitemapVersionLevel({ ...sitemapVersionLevel, cadConfig: { ...sitemapVersionLevel.cadConfig, sourceEpsgCode: value?.code } })}
                    sx={{ width: "50%" }}
                />
            </div>
            <div>
                <StyledFieldLabel>4 Control Points {text}</StyledFieldLabel>
                <TextField
                    value={sitemapVersionLevel.cadConfig?.controlPoints != null ? controlPointsToString(sitemapVersionLevel.cadConfig?.controlPoints) : ""}
                    onChange={(e) => {
                        try {
                            const { value } = e.target;
                            if (value === "") {
                                setSitemapVersionLevel({ ...sitemapVersionLevel, cadConfig: { ...sitemapVersionLevel.cadConfig, controlPoints: null } });
                                setControlPointsError(false);
                                return;
                            }
                            const controlPoints = parseControlPoints(value);
                            setSitemapVersionLevel({ ...sitemapVersionLevel, cadConfig: { ...sitemapVersionLevel.cadConfig, controlPoints } });
                            setControlPointsError(false);
                        } catch (error) {
                            setControlPointsError(true);
                        }
                    }}
                    placeholder="Control Points"
                    sx={{ width: "50%" }}
                />
                {controlPointsError && <Body1>Invalid Control points</Body1>}
            </div>

            <Accordion sx={{ width: "50%", mt: 2, boxShadow: "none", background: "none", border: 0, p: 0 }}>
                <AccordionSummary expandIcon={<FontAwesomeIcon icon={["fas", "chevron-down"]} />} sx={{ p: 0, minHeight: 0, "& .MuiAccordionSummary-content": { m: 0, p: 0 } }}>
                    Advanced Settings
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <div>
                        <StyledFieldLabel>Cad Converter Version</StyledFieldLabel>
                        <FormControl fullWidth>
                            <Select
                                labelId="cad-converter-version-label"
                                value={sitemapVersionLevel.cadConfig?.version ?? ""}
                                onChange={(e) => {
                                    setSitemapVersionLevel({
                                        ...sitemapVersionLevel,
                                        cadConfig: {
                                            ...sitemapVersionLevel.cadConfig,
                                            version: Number(e.target.value),
                                        },
                                    });
                                }}
                                sx={{
                                    fontSize: "1.1rem",
                                    "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                    },
                                }}
                            >
                                {[...cadConverterVersions]
                                    .sort((a, b) => b - a)
                                    .map((version, idx) => (
                                        <MenuItem key={version} value={version}>
                                            {version} {idx === 0 ? <span>(Recommended)</span> : ""}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </div>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

function parseControlPoints(input: string) {
    // Match the two groups inside curly braces
    // eslint-disable-next-line no-useless-escape
    const groups = [...input.matchAll(/\{([^\}]*)\}/g)];
    if (groups.length !== 2) throw new Error("Input must contain exactly two groups of points.");

    // Helper to parse a group into an array of [x, y]
    function parseGroup(groupStr: string) {
        return [...groupStr.matchAll(/\(([^)]+)\)/g)].map((match) => {
            const [x, y] = match[1].split(",").map((s) => parseFloat(s.trim()));
            return [x, y];
        });
    }

    const sourcePoints = parseGroup(groups[0][1]);
    const targetPoints = parseGroup(groups[1][1]);

    if (sourcePoints.length !== targetPoints.length) throw new Error("Source and target must have the same number of points.");

    // Build array of { source, target }
    return sourcePoints.map((src, i) => ({
        source: src,
        target: targetPoints[i],
    }));
}

function controlPointsToString(controlPoints: CadUploadConfigControlPoint[]) {
    if (!Array.isArray(controlPoints) || controlPoints.length === 0) return "";

    const source = controlPoints.map((cp) => `(${cp.source[0]}, ${cp.source[1]})`).join(", ");
    const target = controlPoints.map((cp) => `(${cp.target[0]}, ${cp.target[1]})`).join(", ");
    return `{${source}}, {${target}}`;
}
